import { ref, shallowRef, toValue, watch, type MaybeRefOrGetter, type Ref, type ShallowRef } from 'vue';

export type LottieAnimationData = Record<string, unknown>;

/**
 * Loads a Bodymovin/Lottie JSON asset for `<lottie-player>`.
 *
 * When `<lottie-player>` is given a URL it delegates to lottie-web's asset loader, whose XHR
 * handler calls `completeData(response)` on the 200 branch without a try/catch. A 200 whose body
 * doesn't parse as JSON (truncated response, CDN error page) yields `null` there and throws an
 * unhandled `TypeError: can't access property "__complete"` — Sentry MOVEPRO-CRM-K6 — while the
 * animation silently never appears.
 *
 * Fetching and parsing here instead means the player receives an object, which routes `load()`
 * down its `animationData` branch and never touches that XHR path. Callers also get a `failed`
 * flag so they can render a fallback rather than an empty box.
 */

const requests = new Map<string, Promise<string>>();

const LOTTIE_REQUIRED_KEYS = ['v', 'ip', 'op', 'layers', 'fr', 'w', 'h'] as const;

function isLottieAnimationData(value: unknown): value is LottieAnimationData {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }

    return LOTTIE_REQUIRED_KEYS.every((key) => key in value);
}

/**
 * Fetches the raw JSON text once per URL. Text rather than a parsed object so that every player
 * gets its own copy — lottie-web mutates the animation data it is handed.
 */
function fetchAnimationSource(src: string): Promise<string> {
    const pending = requests.get(src);

    if (pending) {
        return pending;
    }

    const request = fetch(src).then((response) => {
        if (!response.ok) {
            throw new Error(`Lottie animation "${src}" responded with status ${response.status}`);
        }

        return response.text();
    });

    requests.set(src, request);

    // A missing animation is usually a transient network/CDN failure, so let the next mount retry.
    request.catch(() => {
        if (requests.get(src) === request) {
            requests.delete(src);
        }
    });

    return request;
}

export function parseLottieAnimation(source: string): LottieAnimationData {
    const parsed: unknown = JSON.parse(source);

    if (!isLottieAnimationData(parsed)) {
        throw new Error('Lottie animation source is not valid Bodymovin animation data');
    }

    return parsed;
}

export function useLottieAnimation(src: MaybeRefOrGetter<string | undefined>): {
    animationData: ShallowRef<LottieAnimationData | null>;
    failed: Ref<boolean>;
} {
    const animationData: ShallowRef<LottieAnimationData | null> = shallowRef(null);
    const failed: Ref<boolean> = ref(false);

    watch(
        () => toValue(src),
        (url) => {
            animationData.value = null;
            failed.value = false;

            if (!url) {
                failed.value = true;

                return;
            }

            fetchAnimationSource(url)
                .then((source) => {
                    if (toValue(src) !== url) {
                        return;
                    }

                    animationData.value = parseLottieAnimation(source);
                })
                .catch(() => {
                    if (toValue(src) !== url) {
                        return;
                    }

                    failed.value = true;
                });
        },
        { immediate: true },
    );

    return { animationData, failed };
}
